import { useTranslation } from 'react-i18next';
import { NewDropdown } from 'app/components';

import './TagItemShortcuts.scss';

export type TagActionType = 'details' | 'navigate' | 'dismiss' | 'create' | 'test' | 'edit' | 'delete' | 'default';

export interface TagAction {
  type: TagActionType;
  onClick: () => void;
  label?: string;
  disabled?: boolean;
  tooltip?: string;
}

interface TagOption {
  value: TagActionType;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  tooltip?: string;
}

interface TagItemShortcutsProps {
  actions: TagAction[];
}

export const TagItemShortcuts = ({ actions }: TagItemShortcutsProps) => {
  // Default configurations for each generic action type
  const { t } = useTranslation(['comments', 'common', 'tag']);

  const defaultActionConfig: Record<TagActionType, { label: string; icon: React.ReactNode }> = {
    details: { label: t('tag:view.details'), icon: <i className="fa-regular fa-file-lines" /> },
    navigate: { label: t('tag:go.to.tag'), icon: <i className="fa-regular fa-location-dot" /> },
    dismiss: { label: t('common:dismiss'), icon: <i className="fa-regular fa-ban" /> },
    create: { label: t('tag:extend.from.concept'), icon: <i className="fa-solid fa-plus" /> },
    test: { label: t('tag:validation.run'), icon: <i className="fa-solid fa-vial" /> },
    edit: { label: t('comments:edit'), icon: <i className="fa-regular fa-pen" /> },
    delete: { label: t('common:delete'), icon: <i className="fa-regular fa-trash" /> },
    default: { label: t('tag:set.default'), icon: <i className="fa-regular fa-star" /> },
  };

  // Build options from actions, using default config or custom label
  const options: TagOption[] = actions.map((action) => {
    const defaultConfig = defaultActionConfig[action.type];
    return {
      value: action.type,
      label: action?.label ?? defaultConfig.label,
      icon: defaultConfig.icon,
      onClick: action?.onClick,
      disabled: action?.disabled,
      tooltip: action?.tooltip,
    };
  });

  const handleAction = (actionType: string) => {
    const option = options.find((opt) => opt.value === actionType);
    if (option && option.onClick) {
      option.onClick();
    } else {
      console.warn(`No handler defined for action: ${actionType}`);
    }
  };

  return (
    <div className="tag-item-shortcuts">
      <NewDropdown
        options={options as TagOption[]}
        currentValue="none"
        onUpdate={(value) => {
          handleAction(value);
        }}
        hideLabel={true}
        variant="transparent"
        toggleIcon={<i className="fa-regular fa-lg fa-ellipsis-vertical" />}
      />
    </div>
  );
};
